import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SandboxSettings {
  // Global toggle - user can override
  enabled: boolean;

  // Smart auto-enable for destructive queries
  autoEnableForDestructive: boolean;

  // Per-database overrides
  databaseOverrides: Record<string, boolean>;
}

interface SandboxStore extends SandboxSettings {
  // Actions
  setEnabled: (enabled: boolean) => void;
  setAutoEnableForDestructive: (enabled: boolean) => void;
  setDatabaseOverride: (databaseId: string, enabled: boolean) => void;
  removeDatabaseOverride: (databaseId: string) => void;

  // Helpers
  isSandboxEnabledForQuery: (queryType: string, databaseId?: string) => boolean;
}

const destructiveQueryTypes = ['UPDATE', 'DELETE', 'DROP', 'TRUNCATE', 'ALTER'];

export const useSandboxStore = create<SandboxStore>()(
  persist(
    (set, get) => ({
      // Default settings
      enabled: true, // Start with sandbox ON for safety
      autoEnableForDestructive: true, // Smart mode enabled by default
      databaseOverrides: {},

      setEnabled: (enabled) => set({ enabled }),

      setAutoEnableForDestructive: (enabled) => set({ autoEnableForDestructive: enabled }),

      setDatabaseOverride: (databaseId, enabled) =>
        set((state) => ({
          databaseOverrides: {
            ...state.databaseOverrides,
            [databaseId]: enabled,
          },
        })),

      removeDatabaseOverride: (databaseId) =>
        set((state) => {
          const { [databaseId]: _, ...rest } = state.databaseOverrides;
          return { databaseOverrides: rest };
        }),

      // Smart logic: Check if sandbox should be enabled for this query
      isSandboxEnabledForQuery: (queryType, databaseId) => {
        const state = get();

        // 1. Check database-specific override first
        if (databaseId && state.databaseOverrides[databaseId] !== undefined) {
          return state.databaseOverrides[databaseId];
        }

        // 2. If auto-enable is on and query is destructive, enable sandbox
        if (state.autoEnableForDestructive && destructiveQueryTypes.includes(queryType.toUpperCase())) {
          return true;
        }

        // 3. Fall back to global setting
        return state.enabled;
      },
    }),
    {
      name: 'moniva-sandbox-settings',
    }
  )
);
